import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

/**
 * Report-share API client (CSAGE-148).
 *
 * Mint returns the plaintext token ONCE — show it in the modal then
 * forget it. Subsequent fetches only see the metadata.
 */

export type ShareMode = 'live' | 'snapshot';
export type ShareAudience = 'internal' | 'customer' | 'partner' | 'regulator';

export interface ReportShare {
  id: string;
  tenantId: string;
  reportId: string;
  mode: ShareMode;
  pinnedRunId: string | null;
  audience: ShareAudience | null;
  label: string;
  createdBy: string | null;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  lastAccessedAt: string | null;
  accessCount: number;
}

export interface MintShareInput {
  mode: ShareMode;
  pinnedRunId?: string | null;
  audience?: ShareAudience | null;
  label?: string;
  /** ISO-8601, or null for "never". */
  expiresAt?: string | null;
}

export interface MintShareResult {
  share: ReportShare;
  /** Shown ONCE. Don't persist. */
  plaintextToken: string;
  /** Path-only viewer URL (`/r/<token>`). */
  viewerUrl: string;
}

const QK_FOR_REPORT = (reportId: string) => ['reports', reportId, 'shares'] as const;

export function useReportShares(
  reportId: string | undefined,
): ReturnType<typeof useQuery<ReportShare[]>> {
  const api = useApi();
  return useQuery<ReportShare[]>({
    queryKey: reportId ? QK_FOR_REPORT(reportId) : ['reports', 'shares', 'idle'],
    queryFn: () => api.fetch<ReportShare[]>(`/reports/${reportId}/shares`),
    enabled: Boolean(reportId),
  });
}

export function useMintShare(
  reportId: string,
): ReturnType<typeof useMutation<MintShareResult, Error, MintShareInput>> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<MintShareResult, Error, MintShareInput>({
    mutationFn: (input) =>
      api.fetch<MintShareResult>(`/reports/${reportId}/shares`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK_FOR_REPORT(reportId) });
    },
  });
}

export function useRevokeShare(
  reportId: string,
): ReturnType<typeof useMutation<{ ok: true }, Error, string>> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => api.fetch<{ ok: true }>(`/shares/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK_FOR_REPORT(reportId) });
    },
  });
}

/** Compose the absolute viewer URL the way recipients will see it.
 *
 *  The viewer route (`/r/:token`) is mounted on the SPA, NOT the API.
 *  In split-host deploys (sage.gocanopy.io ↔ api.sage.gocanopy.io)
 *  the SPA lives on the web host and the kiosk endpoint on the API
 *  host — but the URL we hand to a recipient is the SPA route, so
 *  we always anchor on `window.location.origin`. (The viewer page
 *  then loads its iframe from VITE_API_BASE_URL internally.)
 */
export function absoluteViewerUrl(viewerUrl: string): string {
  if (typeof window !== 'undefined') return `${window.location.origin}${viewerUrl}`;
  return viewerUrl;
}
